import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';
import { useNotifications } from '../../../providers/NotificationsProvider';
import { useOrganizations } from '../../organization/providers';

import { useCreateShareMutation } from './useCreateShareMutation';

const useCreateShare = () => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  const createShareMutation = useCreateShareMutation(accessToken, currentOrganization?.id);

  const getNotificationLabel = useCallback((type, name) => {
    switch (type) {
      case 'Share':
        return `We've shared this with ${name} and notified them.`;
      case 'Access':
      default:
        return `We've requested ${name} to provide you access to this answer.`;
    }
  }, []);

  return useCallback(
    async (body, type, name) => {
      try {
        const response = await createShareMutation.mutateAsync({
          ...body
        });

        const shareableType = body.shareable_type.toLowerCase();

        if (shareableType === 'space') {
          // Update spaces query data
          queryClient.setQueryData(['chat', 'spaces', currentOrganization?.id], (oldData) => {
            if (!oldData) return oldData;

            return oldData.map((space) => {
              if (space.id === body.shareable_id) {
                // If there's an existing share, merge the participants
                if (space.shares?.[0]) {
                  return {
                    ...space,
                    shares: [
                      {
                        ...space.shares[0],
                        participants: [
                          ...space.shares[0].participants,
                          ...response.data.participants
                        ]
                      }
                    ]
                  };
                }
                // If no existing share, add the new one
                return {
                  ...space,
                  shares: [response.data]
                };
              }
              return space;
            });
          });
        } else if (shareableType === 'conversation') {
          // Update conversations query data
          queryClient.setQueryData(['chat', 'conversations', body.shareable_id], (oldData) => {
            if (!oldData) return oldData;

            if (oldData.shares?.[0]) {
              return {
                ...oldData,
                shares: [
                  {
                    ...oldData.shares[0],
                    participants: [...oldData.shares[0].participants, ...response.data.participants]
                  }
                ]
              };
            }
            return {
              ...oldData,
              shares: [response.data]
            };
          });
        }

        addNotification({
          icon: 'user-plus',
          message: getNotificationLabel(type, name)
        });
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Failed to sent ${type.toLowerCase()} request`
        });
      }
    },
    [createShareMutation, currentOrganization?.id, queryClient]
  );
};

export { useCreateShare };
